import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export default function DomainEvidence() {
  const { domain } = useOutletContext<{ domain: { id: string; domain_name: string } }>();
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const { data, error } = await supabase.functions.invoke('owasp-evidence-export', {
        body: { domainId: domain.id },
      });
      if (error || data?.error) throw new Error(error?.message || data?.error || 'Export failed');
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `protection-evidence-${domain.domain_name}.json`;
      anchor.click();
      URL.revokeObjectURL(url);
      toast.success('Evidence export generated.');
    } catch (err: any) {
      toast.error(err?.message || 'Unable to export evidence');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card className="border-border/60">
      <CardHeader>
        <CardTitle>Export Security Evidence</CardTitle>
        <CardDescription>
          Download a JSON snapshot of protection coverage status, security control evidence, and recent security activity for this domain.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2">
          <Badge variant="outline">Evidence export</Badge>
          <span className="text-sm text-muted-foreground">Includes coverage snapshot, security controls, posture, and recent security activity.</span>
        </div>
        <Button onClick={handleExport} disabled={exporting}>
          {exporting ? 'Exporting...' : 'Export Security Evidence'}
        </Button>
      </CardContent>
    </Card>
  );
}
